import { useEffect, useMemo } from 'react';
import { Row, Col } from 'reactstrap';
import { Country, State } from 'country-state-city';
import { SelectInput, TextInput, stateOptions } from './Inputs';

const countryOptions = Country.getAllCountries().map((c) => ({
    label: c.name,
    value: c.isoCode,
}));

type CountryStateFieldsProps = {
    control: any;
    watch: any;
    setValue: (name: string, value: any, options?: any) => void;
    countryFieldName?: string;
    stateFieldName?: string;
    stateProvinceFieldName?: string;
    zipFieldName?: string;
    required?: boolean;
};

export const CountryStateFields = ({
    control,
    watch,
    setValue,
    countryFieldName = 'country',
    stateFieldName = 'state',
    stateProvinceFieldName = 'state_province',
    zipFieldName = 'zip_code',
    required = false,
}: CountryStateFieldsProps) => {
    const selectedCountry = watch(countryFieldName);
    const isUS = !selectedCountry || selectedCountry === 'US';

    const subdivisionOptions = useMemo(() => {
        if (isUS || !selectedCountry) return [];
        return State.getStatesOfCountry(selectedCountry).map((s) => ({
            label: s.name,
            value: s.name,
        }));
    }, [selectedCountry, isUS]);

    // Reset state fields when country changes
    useEffect(() => {
        setValue(stateFieldName, '', { shouldDirty: false });
        setValue(stateProvinceFieldName, '', { shouldDirty: false });
    }, [selectedCountry]);

    return (
        <>
            <SelectInput
                control={control}
                name={countryFieldName}
                label='Country'
                options={countryOptions}
                required={required}
            />
            <Row>
                <Col sm={isUS ? '8' : subdivisionOptions.length > 0 ? '8' : '12'}>
                    <TextInput
                        label='City'
                        name='city'
                        type='text'
                        control={control}
                    />
                </Col>
                {isUS && (
                    <Col sm='4'>
                        <SelectInput
                            control={control}
                            name={stateFieldName}
                            label='State'
                            options={stateOptions}
                        />
                    </Col>
                )}
                {!isUS && subdivisionOptions.length > 0 && (
                    <Col sm='4'>
                        <SelectInput
                            control={control}
                            name={stateProvinceFieldName}
                            label='State/Province'
                            options={subdivisionOptions}
                        />
                    </Col>
                )}
            </Row>
            <TextInput
                name={zipFieldName}
                label={isUS ? 'Zip Code' : 'Postal Code'}
                type={isUS ? 'zip' : 'text'}
                control={control}
            />
        </>
    );
};
